import React, { useCallback, useEffect, useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Button,
  IconButton,
  Backdrop,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./SetupMpn.scss";
import {
  useLazyCreateMpinQuery,
  useLazyForgetMpinQuery,
} from "../../Feature_Login/Login_Api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { updateLoginData } from "../../Feature_Login/Login_Slice";
import { updateLoadingState } from "../../Feature_Loading/Loading_Slice";
import CustomNetworkImage from "../../../components/CustomNetworkImage/CustomNetworkImage";
import ABCLogo from "../../../assets/images/ABC-Service.jpg";
import CustomThreeDotsLoader from "../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateDeviceID,
  getMobileOperatingSystem,
} from "../../../utils/helper";
import CsLocalStorageUtils from "../../../utils/CsLocalStorageUtils";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { CleverTapEventType } from "../../../utils/CleverTapConstant";
import { CT_ScreenEvntDtls } from "../../Feature_CleverTap/ScreenEvents/CleverTapEventDetails";
import { GTAScreenType, SCREEN_TYPE } from "../../../utils/constants";
import TriggerGTAEvent from "../../../utils/GTA";
import { Colors } from "../../../styles/constant";
import { R_HomeRoutes } from "../../../utils/Routes";
import CustomOnePlatformBox from "../../../components/CustomOnePlatformBox/CustomOnePlatformBox";
import dragHandle from "../../../assets/icons/Drag-Handle.svg";

interface MpinInputProps {
  length?: number;
}

const SetupMpn: React.FC<MpinInputProps> = ({ length = 4 }) => {
  const [mpnvisible, setMpnvisible] = useState<boolean>(true);
  const [confirmmpnvisible, setConfirmmpnvisible] = useState<boolean>(true);
  const [mpin, setMpin] = useState<string[]>(Array(length).fill(""));
  const [confirmMpin, setConfirmMpin] = useState<string[]>(
    Array(length).fill("")
  );
  const [isMatched, setIsMatched] = useState<boolean | null>(null);
  const appDispatch = useAppDispatch();
  const [createMpinApi] = useLazyCreateMpinQuery();
  const [forgetMpin] = useLazyForgetMpinQuery();
  const csUserId = useAppSelector((state) => state.CsAppSlice?.csUserId);
  const loginSlice = useAppSelector((state) => state.LoginSlice);

  const cmsData = useAppSelector((state) => state.CmsSlice.CmsPageVariableData);
  const cmsLogo =
    cmsData.find((i) => i.attributes.cmsType === "ABC Logo")?.attributes.image
      .data?.attributes.url ?? "";
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    triggerCleverTapEvent(CleverTapEventType.SCREEN_EVENT, {
      ScreenName: CT_ScreenEvntDtls.R_GAE.gaLoginSetMpinDetails.screenName,
      ScreenType: SCREEN_TYPE.LoginScreenType,
    });
    appDispatch(updateLoadingState(false));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    setField: React.Dispatch<React.SetStateAction<string[]>>,
    field: string[]
  ) => {
    const value = e.target.value;
    if (value.match(/^\d$/)) {
      const newField = [...field];
      newField[index] = value;
      setField(newField);

      if (index < length - 1) {
        const nextField = document.getElementById(
          `${setField === setMpin ? "mpin-input" : "confirm-mpin-input"}-${
            index + 1
          }`
        );
        if (nextField) (nextField as HTMLInputElement).focus();
      }
    }
  };

  const handleKeyDown = (
    e: any,
    index: number,
    setField: React.Dispatch<React.SetStateAction<string[]>>,
    field: string[]
  ) => {
    if (e.key === "Backspace") {
      if (field[index]) {
        const newField = [...field];
        newField[index] = "";
        setField(newField);
      } else if (index > 0) {
        const prevField = document.getElementById(
          `${setField === setMpin ? "mpin-input" : "confirm-mpin-input"}-${
            index - 1
          }`
        );
        if (prevField) (prevField as HTMLInputElement).focus();
      }
    }
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default behavior
      handleConfirm(); // Call your confirm function
    }
  };

  const handleClickShowPasswordMpn = () => {
    setMpnvisible(!mpnvisible);
  };

  const handleClickShowPasswordconfirmMpn = () => {
    setConfirmmpnvisible(!confirmmpnvisible);
  };

  const handleCleverTapCTAClick = (itemSelected: string) => {
    triggerCleverTapEvent("serv_setup_mpin_cta_click", {
      ScreenName: "Servicing Personal Details Authentication Successful Screen",
      ScreenType: SCREEN_TYPE.generalAppType,
      isPrevPageNameRequired: true,
      ctaText: itemSelected,
    });
    TriggerGTAEvent("serv_setup_mpin_cta_click", {
      screen_type: GTAScreenType.LoginScreenType,
      screen_name: "servicing_login_setup_mpin_details_enter_screen",
      CTA_text: "continue",
    });
  };

  const handleConfirm = () => {
    handleCleverTapCTAClick("Continue");
    setIsMatched(null);
    if (mpin.join("") !== confirmMpin.join("")) {
      setIsMatched(false);
      return;
    }
    const saveMpinNGotoHome = () => {
      appDispatch(updateLoginData({ key: "mpin", value: mpin.join("") }));
      CsLocalStorageUtils.set("MPIN_FOR_MOBILE", loginSlice.mobilenumber);
      navigate(R_HomeRoutes.home, { replace: true });
    };
    const showTechError = (err?: Error) => {
      appDispatch(
        setError({
          error: err || new Error("Error"),
          message: err?.message || "Change Mpin API failed",
        })
      );
    };
    if (state?.type === "forgetMpin") {
      if (mpin.join("") === confirmMpin.join("")) {
        setLoading(true);
        forgetMpin({
          mpin: mpin.join(""),
          csUserId: csUserId || "",
          mobileNo: loginSlice.mobilenumber || "",
          deviceType: getMobileOperatingSystem(),
          deviceId: generateDeviceID(),
        })
          .then((response) => {
            if (response.data?.code === 1) {
              saveMpinNGotoHome();
              setIsMatched(true);
            } else {
              showTechError();
            }
          })
          .catch((error) => {
            showTechError(error);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setIsMatched(false);
      }
    } else {
      if (mpin.join("") === confirmMpin.join("")) {
        setLoading(true);
        createMpinApi({
          mpin: mpin.join(""),
          csUserId: csUserId || "",
          mobileNo: loginSlice.mobilenumber || "",
          deviceType: getMobileOperatingSystem(),
          deviceId: generateDeviceID(),
        })
          .then((response) => {
            if (response.data?.code === 1) {
              saveMpinNGotoHome();
              setIsMatched(true);
            } else {
              showTechError();
            }
          })
          .catch((error) => {
            showTechError();
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setIsMatched(false);
      }
    }
  };

  const logoImageStyle = {
    height: "2.75rem",
    width: "7.125rem",
  };

  return (
    <Box
      className="setup-mpn-wrapper"
      sx={{ height: getMobileOperatingSystem() === "iOS" ? "90vh" : "100vh" }}
    >
      <Box className="header-section">
        <CustomNetworkImage
          fallBackImage={<img src={ABCLogo} alt="" style={logoImageStyle} />}
          src={cmsLogo}
          alt="abc-logo-img"
          style={logoImageStyle}
        />
      </Box>
      {<CustomOnePlatformBox />}

      <Box className="cardContentLayout">
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          marginBottom="1.5rem"
          alignItems="center"
        >
          <img src={dragHandle} className="drag-handler" />
        </Box>
        <Box className="inside-layout-wrapper">
          <Typography
            fontWeight={700}
            fontSize={24}
            marginBottom="0.5rem"
            lineHeight={"1.8rem"}
          >
            Set-up mPIN
          </Typography>
          <Typography variant="body1" color={Colors.descriptionGray}>
            Set mPIN for making login easy and secure
          </Typography>
          <Typography variant="h5" marginTop="1rem" marginBottom=".375rem">
            Set your 4 digit mPIN
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" justifyContent="space-between" width="66%">
              {mpin.map((value, index) => (
                <TextField
                  key={`mpin-${index}`}
                  id={`mpin-input-${index}`}
                  value={value}
                  type={mpnvisible ? "password" : "text"}
                  onChange={(e) => handleChange(e, index, setMpin, mpin)}
                  onKeyDown={(e) => handleKeyDown(e, index, setMpin, mpin)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center" },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  className="mpn-textfield"
                />
              ))}
            </Box>
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPasswordMpn}
            >
              {mpnvisible ? (
                <VisibilityOff style={{ fontSize: "1rem", color: "black" }} />
              ) : (
                <Visibility style={{ fontSize: "1rem", color: "black" }} />
              )}
            </IconButton>
          </Box>

          <Typography variant="h5" marginTop="1rem" marginBottom=".375rem">
            Confirm mPIN
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" justifyContent="space-between" width="66%">
              {confirmMpin.map((value, index) => (
                <TextField
                  key={`confirm-mpin-${index}`}
                  id={`confirm-mpin-input-${index}`}
                  value={value}
                  type={confirmmpnvisible ? "password" : "text"}
                  onChange={(e) =>
                    handleChange(e, index, setConfirmMpin, confirmMpin)
                  }
                  onKeyDown={(e) =>
                    handleKeyDown(e, index, setConfirmMpin, confirmMpin)
                  }
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center" },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  className="mpn-textfield"
                />
              ))}
            </Box>
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPasswordconfirmMpn}
            >
              {confirmmpnvisible ? (
                <VisibilityOff style={{ fontSize: "1rem", color: "black" }} />
              ) : (
                <Visibility style={{ fontSize: "1rem", color: "black" }} />
              )}
            </IconButton>
          </Box>

          {isMatched === false && (
            <Typography color="error" marginTop="1rem">
              MPINs do not match. Please try again.
            </Typography>
          )}
          {isMatched === true && (
            <Typography color="primary">MPIN set successfully!</Typography>
          )}

          <Box
            display="flex"
            justifyContent="center"
            marginTop="1.25rem"
            // marginBottom={{ xs: "1.875rem", sm: 0 }}
          >
            {loading ? (
              <CustomThreeDotsLoader />
            ) : (
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={
                  mpin.some((digit) => digit === "") ||
                  confirmMpin.some((digit) => digit === "")
                }
                sx={{
                  width: "100%",
                  height: "3rem",
                  borderRadius: "1.25rem",
                  // marginBottom: "2.125rem",
                  "&:hover": {
                    backgroundColor: "rgba(175, 179, 192, 1)",
                  },
                }}
              >
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SetupMpn;
